import React, {useState, useEffect} from 'react'
import {useEditorRuntime} from '../../hooks/useEditorRuntime'

/**
 * DevTools panel: state inspector, action log, time-travel slider, export.
 * Uses devTools bridge API; re-renders on state changes via runtime.subscribe.
 */
function EditorDevToolsPanel({devTools, runtime}) {
    const {state} = useEditorRuntime(runtime)
    const [, setTick] = useState(0)

    useEffect(() => {
        if (!runtime) return
        const unsubscribe = runtime.subscribe(() => setTick((t) => t + 1))
        return () => unsubscribe?.()
    }, [runtime])

    if (!devTools) return null

    const log = devTools.getLog()
    const currentIndex = devTools.getCurrentIndex()
    const stateSnapshot = devTools.getStateAt(currentIndex)

    function handleReplaceStateAt(index) {
        devTools.replaceStateAt(index)
    }

    function handleExport() {
        const json = devTools.exportStateJSON()
        const blob = new Blob([json], {type: 'application/json'})
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `editor-state-${Date.now()}.json`
        a.click()
        URL.revokeObjectURL(url)
    }

    function stateToJson(value) {
        if (value instanceof Map) {
            return Object.fromEntries(value)
        }
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            const out = {}
            for (const key of Object.keys(value)) {
                out[key] = stateToJson(value[key])
            }
            return out
        }
        return value
    }

    const displayState = stateSnapshot != null ? stateSnapshot : state ? stateToJson(state) : null
    const stateJson = displayState != null ? JSON.stringify(displayState, null, 2) : 'No state recorded yet.'

    return (
        <section className="SidebarPanel EditorDevToolsPanel" aria-label="Editor DevTools">
            <header className="SidebarPanel__Header">
                <h2 className="SidebarPanel__Title">DevTools</h2>
            </header>
            <div className="SidebarPanel__Content EditorDevToolsPanel__Content">
                {/* Time-travel */}
                {log.length > 0 && (
                    <div className="EditorDevToolsPanel__Section">
                        <h3 className="EditorDevToolsPanel__SectionTitle">Time-travel</h3>
                        <div className="EditorDevToolsPanel__TimeTravel">
                            <input
                                type="range"
                                min={0}
                                max={Math.max(0, log.length - 1)}
                                value={currentIndex}
                                onChange={(e) => handleReplaceStateAt(Number(e.target.value))}
                                className="EditorDevToolsPanel__Slider"
                                aria-label="Time-travel to state"
                            />
                            <span className="EditorDevToolsPanel__StepLabel">
                                {currentIndex + 1} / {log.length}
                            </span>
                        </div>
                    </div>
                )}

                {/* Action log */}
                <div className="EditorDevToolsPanel__Section">
                    <h3 className="EditorDevToolsPanel__SectionTitle">Action log</h3>
                    <ul className="EditorDevToolsPanel__LogList" role="list">
                        {log.length === 0 ? (
                            <li className="EditorDevToolsPanel__LogEmpty">No actions yet.</li>
                        ) : (
                            log.map((entry, index) => (
                                <li key={entry.id}>
                                    <button
                                        type="button"
                                        className={`EditorDevToolsPanel__LogEntry ${index === currentIndex ? 'is-current' : ''}`}
                                        onClick={() => handleReplaceStateAt(index)}
                                        title={`Go to step ${index + 1}`}
                                    >
                                        <span className="EditorDevToolsPanel__LogIndex">{index + 1}</span>
                                        <span className="EditorDevToolsPanel__LogSource">{entry.source}</span>
                                        {entry.command != null && (
                                            <span className="EditorDevToolsPanel__LogCommand">
                                                {entry.command.type}
                                            </span>
                                        )}
                                    </button>
                                </li>
                            ))
                        )}
                    </ul>
                </div>

                {/* State inspector */}
                <div className="EditorDevToolsPanel__Section">
                    <h3 className="EditorDevToolsPanel__SectionTitle">State</h3>
                    <pre className="EditorDevToolsPanel__StatePreview">{stateJson}</pre>
                </div>

                {/* Export */}
                <div className="EditorDevToolsPanel__Section">
                    <button type="button" className="EditorDevToolsPanel__ExportButton" onClick={handleExport}>
                        Export state JSON
                    </button>
                </div>
            </div>
        </section>
    )
}

export default EditorDevToolsPanel
